import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { IdentityProviderDto } from 'src/app/services/dtos'
import { ModalDialogComponent } from '../../../../shared/modal-dialog/modal-dialog.component'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from 'src/app/account/services/account.service'

export type IdpType = 'saml' | 'msEntra'
export type IdpOperation = 'create' | 'edit'

export interface IdpModalData {
  type: IdpType
  operation: IdpOperation
  existingIdp?: IdentityProviderDto
}

export interface IdpModalResult {
  name: string
  metadata?: string
  tenantId?: string
  useGlobalSpEntityId?: boolean
  autoCreateSubjects: boolean
}

@Component({
  selector: 'app-idp-modal',
  templateUrl: './idp-modal.component.html',
  styleUrls: ['./idp-modal.component.scss'],
  standalone: false,
})
export class IdpModalComponent {
  @ViewChild('dialog', { static: false }) dialog: ModalDialogComponent
  @Input() accountId: string
  @Output() save = new EventEmitter<IdpModalResult>()
  @Output() cancel = new EventEmitter<void>()

  form: UntypedFormGroup
  data: IdpModalData = { type: 'saml', operation: 'create' }

  constructor(
    private formBuilder: UntypedFormBuilder,
    public apiService: ApiService,
    public uiService: UiService,
    private accountService: AccountService
  ) {
    this.initializeForm()
  }

  private initializeForm() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      metadata: [''],
      tenantId: [''],
      useGlobalSpEntityId: [true],
      autoCreateSubjects: [false],
    })
  }

  open(data: IdpModalData) {
    this.data = data
    this.updateFormValidators()
    this.populateForm()
    this.dialog.open()
  }

  close() {
    this.dialog.close()
  }

  private updateFormValidators() {
    const metadataControl = this.form.get('metadata')
    const tenantIdControl = this.form.get('tenantId')

    if (this.data.type === 'saml') {
      metadataControl.setValidators([Validators.required])
      tenantIdControl.clearValidators()
    } else {
      tenantIdControl.setValidators([Validators.required])
      metadataControl.clearValidators()
    }

    metadataControl.updateValueAndValidity()
    tenantIdControl.updateValueAndValidity()
  }

  private populateForm() {
    if (this.data.operation === 'create') {
      // Default to using the "global" entity ID for new SAML providers.  Before 2023-07-11,
      // SAML providers used an account-specific entity ID, but that causes problems for
      // users using SaaS IdPs that don't allow users to relax constraints on entity
      // matching (Okta, Azure, etc.).  We don't give users any UI to change this value,
      // but the API fully supports flipping it back and forth if we wanted to.
      this.form.reset({
        name: '',
        metadata: '',
        tenantId: '',
        useGlobalSpEntityId: true,
        autoCreateSubjects: false,
      })
    } else if (this.data.existingIdp) {
      const idp = this.data.existingIdp
      // SAML providers configured before 2023-07-11 will have a value of "false",
      // which we must preserve during updates to avoid breaking authentication flows.
      this.form.patchValue({
        name: idp.name,
        metadata: idp.config.metadata || '',
        tenantId: idp.config.tenantId || '',
        useGlobalSpEntityId: idp.config.useGlobalSpEntityId || true,
        autoCreateSubjects: idp.config.autoCreateSubjects || false,
      })
    }
  }

  onSave() {
    if (this.form.valid) {
      this.save.emit(this.form.value as IdpModalResult)
    }
  }

  onCancel() {
    this.cancel.emit()
    this.close()
  }

  get title(): string {
    if (this.data.operation === 'create') {
      return this.data.type === 'saml' ? 'Tell us about your identity provider' : 'Add Microsoft Entra Directory'
    } else {
      return this.data.type === 'saml' ? 'Tell us about your identity provider' : 'Edit Microsoft Entra Directory'
    }
  }

  get isSaml(): boolean {
    return this.data.type === 'saml'
  }

  get isMsEntra(): boolean {
    return this.data.type === 'msEntra'
  }

  get isCreate(): boolean {
    return this.data.operation === 'create'
  }

  get isEdit(): boolean {
    return this.data.operation === 'edit'
  }

  audienceUri(): string {
    if (this.form.value.useGlobalSpEntityId) {
      return this.apiService.globalAudienceUri()
    } else {
      return this.apiService.accountAudienceUri(this.accountId)
    }
  }

  get loading(): boolean {
    return this.dialog?.loading || false
  }

  set loading(value: boolean) {
    if (this.dialog) {
      this.dialog.loading = value
    }
  }

  get error(): string {
    return this.dialog?.error || ''
  }

  set error(value: string) {
    if (this.dialog) {
      this.dialog.error = value
    }
  }

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }
}
